// Ported from dev-scrips-pm-react src/billing/lib/claim-diagnosis.ts (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type {
  AssessmentPlanCondition,
  DiagnosisCodeOption,
  EncounterAssessmentPlan,
} from '@/billing/services/use-claim-diagnosis';

/**
 * Pure A&P condition-list mutations for the Review Claim diagnosis editor
 * (PROD-969c phase 2). These NEVER touch any field other than
 * `assessmentPlanCondition` — every other slot on the A&P (medications,
 * procedures, snomed, vaccinations, radiology, referrals, notes, flags) is
 * spread through verbatim. This is the data-integrity safeguard: a diagnosis
 * edit must not reshape the patient's clinical record. See
 * `docs/CONVERSIONS/claim-969c/CONVERSION_NOTES.md`.
 */

/**
 * No-Z ISO with millis (billing-surface date convention), read off the LOCAL
 * clock.
 *
 * PROD-1778: was `new Date().toISOString().replace(/Z$/, '')`. Stripping the
 * `Z` from a value `toISOString()` has already converted to UTC does not
 * restore local time — it bakes the offset in, and the .NET binder stores the
 * literal (13:00 Asia/Dubai landed as 09:00). Delegate to the one helper that
 * reads local clock components.
 */
function nowNoZ(): string {
  return formatLocalDatetimeForWire(new Date());
}

/** Visible (non-deleted) conditions, in order. */
export function activeConditions(
  ap: EncounterAssessmentPlan | undefined,
): AssessmentPlanCondition[] {
  return (ap?.assessmentPlanCondition ?? []).filter((c) => !c.isDeleted);
}

/**
 * Append a new ICD-10 condition. Mirrors the captured add curl: `identifier`
 * and `assessmentPlanId` are null (server assigns), `isPrimary:false`,
 * `onSetDate` = now, empty `childMedicalCode` sub-arrays. Returns a NEW A&P
 * with everything else preserved.
 */
export function addDiagnosisCondition(
  ap: EncounterAssessmentPlan,
  option: DiagnosisCodeOption,
): EncounterAssessmentPlan {
  const newCondition: AssessmentPlanCondition = {
    identifier: null,
    encounter: ap.encounter ?? null,
    parentId: null,
    isPrimary: false,
    childMedicalCode: {
      assessmentPlanCondition: [],
      assessmentPlanProcedures: [],
      assessmentPlanSnomed: [],
      assessmentPlanMedication: [],
      assessmentPlanVaccination: [],
      radiologyOrderRequest: [],
    },
    conditionCode: {
      code: option.code,
      display: option.display,
      system: option.system,
    },
    externalCode: null,
    assessmentPlanId: null,
    subject: ap.subject ?? null,
    performer: ap.performer ?? null,
    notes: null,
    onSetDate: nowNoZ(),
    endDate: null,
    isDeleted: false,
  };
  return {
    ...ap,
    assessmentPlanCondition: [
      ...(ap.assessmentPlanCondition ?? []),
      newCondition,
    ],
  };
}

/**
 * Remove a condition by its `identifier` (precise — avoids duplicate-code
 * ambiguity). Drops it from the list (observed remove behaviour). All other
 * conditions — and the rest of the A&P — pass through untouched.
 */
export function removeDiagnosisCondition(
  ap: EncounterAssessmentPlan,
  identifier: string,
): EncounterAssessmentPlan {
  return {
    ...ap,
    assessmentPlanCondition: (ap.assessmentPlanCondition ?? []).filter(
      (c) => c.identifier !== identifier,
    ),
  };
}

/**
 * Make the condition with `identifier` the SOLE primary — sets it `isPrimary:true`
 * and every other condition `isPrimary:false`. A claim must have exactly one
 * principal diagnosis (Flutter `review_claim_popup` Primary/Secondary dropdown;
 * demoting the only primary is blocked in the UI with "One diagnosis must be
 * Primary."). Everything else on the A&P passes through untouched.
 */
export function setPrimaryDiagnosis(
  ap: EncounterAssessmentPlan,
  identifier: string,
): EncounterAssessmentPlan {
  return {
    ...ap,
    assessmentPlanCondition: (ap.assessmentPlanCondition ?? []).map((c) =>
      c.identifier === identifier
        ? { ...c, isPrimary: true }
        : { ...c, isPrimary: false },
    ),
  };
}

/** Count of active conditions currently flagged primary. */
export function primaryCount(ap: EncounterAssessmentPlan | undefined): number {
  return activeConditions(ap).filter((c) => c.isPrimary).length;
}

/**
 * True when adding `option` would duplicate an existing active condition.
 * Case-insensitive on the code — ICD-10 codes are canonically upper-case, but a
 * stale-cache row could arrive unnormalised; we don't want dedup to silently
 * miss in that case (Samer review).
 */
export function isDuplicateDiagnosis(
  ap: EncounterAssessmentPlan | undefined,
  option: DiagnosisCodeOption,
): boolean {
  const target = option.code?.trim().toUpperCase();
  if (!target) return false;
  return activeConditions(ap).some(
    (c) => c.conditionCode?.code?.trim().toUpperCase() === target,
  );
}
